/**
 * Call-environment gate — decides whether placing/receiving a WebRTC call is even POSSIBLE in
 * the current runtime, so the dialer can hard-block instead of failing silently mid-call.
 *
 * Why: iOS "Add to Home Screen" standalone web apps hit a long-standing WebKit bug where
 * getUserMedia gets no mic permission (navigator.mediaDevices can be undefined) — calls simply
 * can't work. Android installed PWAs do NOT have this problem, so we must NOT block them.
 *
 * Every function takes an injectable `win` (defaults to the real window) so the logic unit-tests
 * deterministically in plain node (callEnvironment.test.js) — no browser, no real devices.
 * CommonJS so node can require() it while webpack/babel interop still lets the .jsx import it.
 */

function _win(win) { return win || (typeof window !== 'undefined' ? window : {}); }

// iOS incl. iPadOS 13+ (which reports as MacIntel with a touch screen).
function isIOS(win) {
  var nav = _win(win).navigator || {};
  var ua = nav.userAgent || '';
  return /iP(hone|od|ad)/.test(ua) || (nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1);
}

// Launched as an installed/home-screen app rather than a normal browser tab.
// iOS exposes navigator.standalone (Apple-only); everyone else uses the display-mode media query.
function isStandalone(win) {
  var w = _win(win);
  var nav = w.navigator || {};
  if (nav.standalone === true) return true;
  try {
    if (typeof w.matchMedia === 'function') {
      return ['standalone', 'fullscreen', 'minimal-ui'].some(function (m) {
        return w.matchMedia('(display-mode: ' + m + ')').matches;
      });
    }
  } catch (e) { /* matchMedia unavailable → fall through */ }
  return false;
}

// The mic API is literally absent → getUserMedia can never succeed (insecure context, stripped
// WebView, etc.). The surest "calls are impossible" signal there is.
function micUnavailable(win) {
  var nav = _win(win).navigator || {};
  return !(nav.mediaDevices && typeof nav.mediaDevices.getUserMedia === 'function');
}

/**
 * Returns a reason code when calling is IMPOSSIBLE in this environment, else null:
 *   'ios-standalone' — iOS home-screen app on pre-16.4 WebKit (no mic API there);
 *                      tell them to open in Safari.
 *   'no-mic'         — the mic API isn't present at all (any other environment).
 *
 * CAPABILITY-detected, not version-sniffed: iOS 16.4 gave home-screen web apps
 * getUserMedia (and wakeLock — the dialer holds one during calls so the screen
 * can't lock mid-call). A standalone iOS app WITH a working mic API is therefore
 * allowed to call; only the old broken runtime gets the send-to-Safari screen.
 */
function callBlockReason(win) {
  var w = _win(win);
  if (micUnavailable(w)) return (isIOS(w) && isStandalone(w)) ? 'ios-standalone' : 'no-mic';
  return null;
}

function callingBlocked(win) { return callBlockReason(win) !== null; }

// Hold a screen wake lock for every LIVE call phase — on a home-screen PWA the
// screen locking kills WebRTC audio mid-call (same failure the AI voice modal had).
function shouldHoldWakeLock(callState) {
  return callState === 'connecting' || callState === 'ringing' || callState === 'connected' || callState === 'incoming';
}

module.exports = {
  isIOS: isIOS,
  isStandalone: isStandalone,
  micUnavailable: micUnavailable,
  callBlockReason: callBlockReason,
  callingBlocked: callingBlocked,
  shouldHoldWakeLock: shouldHoldWakeLock,
};
